import "./Avatar.css";

export type PresenceStatus = "online" | "away" | "offline";

export interface AvatarProps {
  name: string;
  size?: number;
  src?: string;
  /** Renders "#" instead of initials, for channel avatars in chat/list rows. */
  channel?: boolean;
  presence?: PresenceStatus;
  className?: string;
}

// Six tones defined in Avatar.css (ui-avatar--tone-0..5). Kept out of tokens.css
// because these are identity colours (hashed per person), not brand semantics.
const TONE_COUNT = 6;

/**
 * Deterministic hash so the same name always lands on the same tone —
 * required so a person's avatar colour is consistent everywhere they appear,
 * without a central colour registry.
 */
function hashName(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash << 5) - hash + name.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const first = words[0]?.[0] ?? "";
  const second = words[1]?.[0] ?? "";
  const initials = (first + second).toUpperCase();
  return initials || "?";
}

export function toneIndexOf(name: string): number {
  return hashName(name) % TONE_COUNT;
}

export function Avatar({ name, size = 34, src, channel = false, presence, className }: AvatarProps) {
  const tone = toneIndexOf(name);
  const content = channel ? "#" : initialsOf(name);

  return (
    <span
      className={["ui-avatar", `ui-avatar--tone-${tone}`, className ?? ""].filter(Boolean).join(" ")}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}
    >
      {src ? (
        <img src={src} alt="" className="ui-avatar__img" />
      ) : (
        <span aria-hidden="true">{content}</span>
      )}
      {presence && (
        <span
          className={`ui-avatar__presence ui-avatar__presence--${presence}`}
          style={{ width: Math.max(8, size * 0.28), height: Math.max(8, size * 0.28) }}
        />
      )}
      <span className="sr-only">{channel ? `channel ${name}` : name}</span>
    </span>
  );
}

export interface AvatarStackProps {
  names: string[];
  max?: number;
  size?: number;
  className?: string;
}

export function AvatarStack({ names, max = 3, size = 26, className }: AvatarStackProps) {
  const visible = names.slice(0, max);
  const overflow = names.length - visible.length;
  const label = names.length === 0 ? "No members" : `Members: ${names.join(", ")}`;

  return (
    <span className={["ui-avatar-stack", className ?? ""].filter(Boolean).join(" ")} role="group" aria-label={label}>
      {visible.map((name, i) => (
        <span
          key={`${name}-${i}`}
          className="ui-avatar-stack__item"
          style={{ zIndex: visible.length - i, marginLeft: i === 0 ? 0 : -Math.round(size * 0.32) }}
        >
          <Avatar name={name} size={size} />
        </span>
      ))}
      {overflow > 0 && (
        <span
          className="ui-avatar-stack__overflow"
          style={{
            width: size,
            height: size,
            fontSize: Math.round(size * 0.34),
            marginLeft: -Math.round(size * 0.32),
          }}
          aria-hidden="true"
        >
          +{overflow}
        </span>
      )}
    </span>
  );
}
